import { useTranslations } from 'next-intl'

import { TRoomDetail } from '@/entities/room'
import { ContentBox, Icon } from '@/shared/ui'

import { Heading, Item, Label, List, Value } from '../../common'

export type StructureProps = Pick<
  TRoomDetail,
  | 'roomCnt'
  | 'bathroomCnt'
  | 'sittingroomCnt'
  | 'cookroomCnt'
  | 'sharedRoom'
  | 'sharedBathroom'
  | 'sharedCookroom'
>

export const Structure = ({
  roomCnt,
  bathroomCnt,
  sittingroomCnt,
  cookroomCnt,
  sharedRoom,
  sharedBathroom,
  sharedCookroom,
}: StructureProps) => {
  const t = useTranslations()

  return (
    <div className="w-[260px] space-y-5">
      <Heading>{t('room_detail.layout.title')}</Heading>
      <List spacing="medium" className="typo-body2 text-gray-900">
        <Item className="justify-between">
          <Label icon="room" className="font-semibold">
            {t('common.bedroom')}
          </Label>
          <Value>
            {sharedRoom
              ? t('room_detail.layout.type.shared_room')
              : t('room_detail.layout.count', { number: roomCnt })}
          </Value>
        </Item>
        <Item className="justify-between">
          <Label icon="restroom" className="font-semibold">
            {t('common.bathroom')}
          </Label>
          <Value>
            {sharedBathroom
              ? t('room_detail.layout.type.shared')
              : t('room_detail.layout.count', { number: bathroomCnt })}
          </Value>
        </Item>
        <Item className="justify-between">
          <Label icon="kitchen" className="font-semibold">
            {t('common.kitchen')}
          </Label>
          <Value>
            {sharedCookroom
              ? t('room_detail.layout.type.shared')
              : t('room_detail.layout.count', { number: cookroomCnt })}
          </Value>
        </Item>
        <Item className="justify-between">
          <Label icon="livingRoom" className="font-semibold">
            {t('common.living_room')}
          </Label>
          <Value>
            {sharedCookroom
              ? t('room_detail.layout.type.shared')
              : t('room_detail.layout.count', { number: sittingroomCnt })}
          </Value>
        </Item>
      </List>
    </div>
  )
}

export type SharedPlaceMessageProps = Pick<
  TRoomDetail,
  'sharedRoom' | 'sharedBathroom' | 'sharedCookroom'
>

export const SharedPlaceMessage = ({
  sharedRoom,
  sharedBathroom,
  sharedCookroom,
}: SharedPlaceMessageProps) => {
  const t = useTranslations('room_detail.layout.shared')

  if (!sharedRoom && !sharedBathroom && !sharedCookroom) {
    return null
  }

  return (
    <div className="flex items-center py-1.5">
      <Icon name="alert-filled" className="text-red-500" size={24} />
      <p className="typo-body2 font-medium text-red-500">
        {(() => {
          if (sharedRoom && sharedBathroom && sharedCookroom) {
            return t('all_structure')
          }

          if (sharedRoom && sharedBathroom) {
            return t('room_bathroom')
          }

          if (sharedRoom && sharedCookroom) {
            return t('room_kitchen')
          }

          if (sharedBathroom && sharedCookroom) {
            return t('bathroom_kitchen')
          }

          if (sharedRoom) {
            return t('room')
          }

          if (sharedBathroom) {
            return t('bathroom')
          }

          if (sharedCookroom) {
            return t('kitchen')
          }

          return ''
        })()}
      </p>
    </div>
  )
}

export type DuplexStructureMessageProps = Pick<TRoomDetail, 'duplexStructure'>

export const DuplexStructureMessage = ({
  duplexStructure,
}: DuplexStructureMessageProps) => {
  const t = useTranslations('room_detail.layout')

  if (!duplexStructure) {
    return null
  }

  return (
    <ContentBox spacing="medium" color="purple" className="space-y-2">
      <p className="typo-label2 font-normal text-gray-900">
        {t.rich('loft', {
          strong: chunks => (
            <strong className="typo-label1 font-semibold text-black">
              {chunks}
            </strong>
          ),
        })}
      </p>
    </ContentBox>
  )
}
